import { columnFormats } from "../constants/constants";
import { tryFindOverride } from "../../charts/TableView/tableCellFormatter";

function getTypeFromMeta(meta, column) {
  return (meta?.fields ?? []).find((field) => field.alias === column)?.type;
}

/**
 * Gets the xlsx format of the given IO column format.
 * @param type {string} IO column format
 * @returns {?string} The xlsx format.
 */
function getNestedColumnFormat(type) {
  return columnFormats.flatMap((f) => f.options).find((f) => f.value === type)
    ?.xlsx;
}

// old formula mapping
function getXLSXFormatFromOldFormula(formula) {
  switch (formula) {
    case "PERCENT":
    case "PERCENT_MINUS_DIVISION":
    case "PERCENT_MINUS_DIVISION_DENOMINATOR":
    case "PERCENT_CANCEL_YOY":
    case "PERCENT_NET_CANCEL_YOY":
    case "VALUE_AS_PERCENT":
    case "PRE_CALCULATED_PERCENT_DIFFERENCE":
    case "DIFFERENCE_ON_DIVISION_PERCENT":
    case "ADDITION_ON_DIVISION_PERCENT":
    case "COMPARE_DIVISION_ON_DIVISION_PERCENT":
    case "DIVISION_PERCENT":
    case "DIVISION_PERCENT_PRECISION_2":
    case "PERCENT_POINT_DIFFERENCE":
    case "DIVISION_PERCENT_MINUS_ONE":
    case "ONE_MINUS_PERCENT":
    case "DIFFERENCE_PERCENT":
    case "PERCENT_DIFFERENCE_PRECISION_2":
    case "DIFF_SUB_DIV_PERCENT":
    case "PERCENT_OVER_PERCENT":
      return "0.00%";

    case "DIVISION_PERCENT_PRECISION_3":
    case "PERCENT_DIFFERENCE_PRECISION_3":
      return "0.000%";

    case "DIFFERENCE_PERCENT_1":
    case "PERCENT_DIFFERENCE":
    case "PERCENT_DIVISION_ADDITION":
    case "DIFF_DIV_PERCENT":
      return "0.0%";

    case "PERCENT_WHOLE":
      return "0%";

    case "INTEGER":
    case "DIFFERENCE_INTEGER":
    case "CONDITIONAL_DIFFERENCE_INTEGER":
    case "ADDITION_INTEGER":
    case "DIFFERENCE_INTEGER_FROM_ADDITION":
    case "DIVISION_0":
    case "DIFF_DIV":
      return "0";

    case "DIFFERENCE_DECIMAL":
    case "DIVISION":
    case "DIFF_DIV_2":
      return "0.00";

    case "DIFFERENCE_DECIMAL_1":
    case "DIVISION_1":
    case "DIVISION_DIFFERENCE":
    case "DIVISION_ADDITION":
    case "DIFF_DIV_1":
      return "0.0";

    case "VALUE_AS_CURRENCY":
    case "DIVISION_CURRENCY":
    case "DIFF_DIV_CURRENCY_2":
      return "$#,##0.00";

    case "DIVISION_CURRENCY_WHOLE":
    case "DIFFERENCE_WHOLE":
    case "CONDITIONAL_DIFFERENCE_WHOLE":
    case "DIFF_DIV_CURRENCY":
    case "DIFFERENCE":
      return "$#,##0";

    case "DIVISION_CURRENCY_TENTH":
    case "DIFF_DIV_CURRENCY_1":
      return "$#,##0.0";

    default:
      return "@";
  }
}

export function generateXLSXFormats(columns, meta) {
  const formats = columns.filter(Boolean).map((column) => {
    const type = getTypeFromMeta(meta, column);

    if (type) return getNestedColumnFormat(type);

    // in new formula format placed in third position
    if (column.includes("snf::")) {
      const [, , format] = column.split("::");
      const xlsxFormat = getNestedColumnFormat(format);
      if (xlsxFormat) return xlsxFormat;
    }

    // in open math and old formula format placed in second position
    const [, format] = column.split("::");
    const xlsxFormat = getNestedColumnFormat(format);
    if (xlsxFormat) return xlsxFormat;
    return getXLSXFormatFromOldFormula(format);
  });

  return formats;
}

function getFormatOverride(formatOverrides, metric, column, dataRow) {
  const formatOverride =
    formatOverrides.find(
      (override) =>
        override.conditionValue === metric.v && override.overrideKey === column
    ) ??
    (dataRow ? tryFindOverride(formatOverrides, dataRow, column) : undefined);

  if (!formatOverride) return;

  let format = getNestedColumnFormat(formatOverride.overrideFormat);

  // Handle precision override
  if (formatOverride.overridePrecision !== undefined && format) {
    const precision = formatOverride.overridePrecision;

    // Adjust the format based on precision
    if (formatOverride.overrideFormat === "percent") {
      if (precision === 0) {
        format = "0%";
      } else if (precision === 1) {
        format = "0.0%";
      } else if (precision === 2) {
        format = "0.00%";
      } else if (precision === 3) {
        format = "0.000%";
      }
    } else if (formatOverride.overrideFormat === "decimal") {
      if (precision === 0) {
        format = "0";
      } else if (precision === 1) {
        format = "0.0";
      } else if (precision === 2) {
        format = "0.00";
      } else if (precision === 3) {
        format = "0.000";
      }
    }
  }

  return format;
}

const dateFormatCharacterRegex = /[ymdhis]/;

function removeNotCoversionSubString(cell, format) {
  if ((cell.v + "").includes(skipConversion)) {
    cell.v = cell.v.replaceAll(skipConversion, "");
  } else if (
    (!format || format === "@") &&
    typeof cell.v === "number" &&
    dateFormatCharacterRegex.test(cell.z ?? "")
  ) {
    // SheetJS recognized it as a date, so don't change the format to literal.
  } else if (typeof cell.v === "number" && cell.z && cell.z !== "@") {
    // The cell already has a non-@ format specified, so keep it.
  } else {
    cell.z = format ?? "@";
  }
}

function formatColumn(
  utils,
  worksheet,
  colLetter,
  format,
  formatOverrides,
  column,
  data,
  headerRowCount,
  pinnedHeader
) {
  const range = utils.decode_range(worksheet["!ref"]);

  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    if (pinnedHeader) {
      const topRow = worksheet[colLetter + 1];

      if (topRow) {
        const format = getNestedColumnFormat(pinnedHeader.exportFormat);
        topRow.z = format;
      }
    }

    const cellRef = colLetter + (row + 1);
    const cell = worksheet[cellRef];

    if (!cell) continue;

    // formatOverrides needs to set format to each cell in the table
    // ex: one column can contain currency, percent, integer and etc.
    if (formatOverrides?.length) {
      // freeze first metric column (usually its first column)
      const metric = worksheet["A" + (row + 1)];
      const format = getFormatOverride(
        formatOverrides,
        metric,
        column,
        data?.[row - headerRowCount]
      );
      removeNotCoversionSubString(cell, format);
      if (format) {
        cell.z = format ?? "@";
        continue;
      }
    }

    removeNotCoversionSubString(cell, format);
  }
}

// Convert column index (0-based) to Excel letter
function columnLetter(colIndex) {
  let letter = "";
  let temp = colIndex + 1;
  while (temp > 0) {
    let mod = (temp - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    temp = Math.floor((temp - mod) / 26);
  }
  return letter;
}

export function formatColumns(
  ws,
  utils,
  subTitles,
  data,
  meta,
  formatOverrides,
  pinnedHeader,
  headerRowCount,
  rowGroupKeys
) {
  const columns = (subTitles ?? []).flat();
  const [rowGroupKey, ...rest] = columns;

  // rowGroupKeys stored separate so we need to include them to columns
  // usually their position is between first column and other grouped columns
  const groupedColumns = rowGroupKeys
    ? [rowGroupKey, ...rowGroupKeys, ...rest]
    : columns;
  const formats = generateXLSXFormats(groupedColumns, meta);

  formats.forEach((format, index) => {
    formatColumn(
      utils,
      ws,
      columnLetter(index),
      format,
      formatOverrides,
      groupedColumns[index],
      data,
      headerRowCount,
      pinnedHeader
    );
  });
}

export const skipConversion = "::skip::conversion";
export const percentNumberRegex = /^[+-]?\d+(\.\d+)?%$/m;
export const percentNumberGlobalRegex = new RegExp(
  percentNumberRegex.source,
  `g${percentNumberRegex.flags}`
);
